import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, fromEventPattern } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment, googlemapskey } from '../../enviroments/environment';
import { TelemetryService } from './telemetry.service';

declare var google: any;


@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private apiKey = googlemapskey.key; 
  private activeApiKey = this.apiKey;
  private readonly mapsReadyCallbackName = '__onGoogleMapsReady';

  private autocompleteService: any;
  private placesService: any;
  private autocompleteInput: HTMLInputElement | null = null;
  private placePhotoSubject = new BehaviorSubject<string | null>(null);

  private apiLoaded = false;
  private geocodeRequestCount = 0;
  private geocodeRequestBySource: Record<string, number> = {};

  constructor(
    private http: HttpClient, 
    private ngZone: NgZone,
    private telemetryService: TelemetryService
  ) {

   }


  getCityByZipCode(zipCode: string): Observable<any> {
    // const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${zipCode}&components=${this.countryRestriction}&key=${this.apiKey}`;
    const url = ``;
    
    return this.http.get<any>(url).pipe(
      map(response => {
        if (response.status === 'OK' && response.results.length > 0) {
          return response.results[0];
        }
        throw new Error('No resuls ftound');
      }),
      catchError(error => {
        console.error('Error in geocodificación:', error);
        throw error;
      })
    );
  }

  loadGoogleMapsApi(apiKey: string): Promise<void> {
    this.activeApiKey = apiKey || this.apiKey;
    const timer = this.telemetryService.startTimer();
    const keyTelemetry = this.getGoogleApiKeyTelemetry();
    if (this.isGoogleMapsReady()) {
      this.apiLoaded = true;
      this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), true, 200, {
        sdkLoadMode: 'already_ready',
        likelyNetworkLoad: false,
        ...keyTelemetry,
      });
      return Promise.resolve();
    }

    if (typeof document === 'undefined') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (this.isGoogleMapsReady()) {
        this.apiLoaded = true;
        this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), true, 200, {
          sdkLoadMode: 'already_ready',
          likelyNetworkLoad: false,
          ...keyTelemetry,
        });
        resolve();
        return;
      }

      const existingScript = document.querySelector('script[data-google-maps-loader="true"]') as HTMLScriptElement | null;
      if (existingScript) {
        this.waitForGoogleMapsReady(
          () => {
            this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), true, 200, {
              sdkLoadMode: 'existing_script_wait',
              likelyNetworkLoad: false,
              ...keyTelemetry,
            });
            resolve();
          },
          (reason?: any) => {
            this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), false, undefined, {
              sdkLoadMode: 'existing_script_wait',
              likelyNetworkLoad: false,
              errorReason: String(reason ?? ''),
              ...keyTelemetry,
            });
            reject(reason);
          }
        );
        return;
      }

      const win = window as any;
      win[this.mapsReadyCallbackName] = () => {
        this.apiLoaded = true;
        this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), true, 200, {
          sdkLoadMode: 'script_injected',
          likelyNetworkLoad: true,
          ...keyTelemetry,
        });
        resolve();
        try {
          delete win[this.mapsReadyCallbackName];
        } catch {
          win[this.mapsReadyCallbackName] = undefined;
        }
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.activeApiKey}&loading=async&libraries=places&region=US&language=en&callback=${this.mapsReadyCallbackName}`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-google-maps-loader', 'true');
      
      script.onerror = () => {
        this.telemetryService.trackApiCallResult('GoogleMapsSdkLoad', 'https://maps.googleapis.com/maps/api/js', this.telemetryService.stopTimer(timer), false, undefined, {
          sdkLoadMode: 'script_error',
          likelyNetworkLoad: true,
          ...keyTelemetry,
        });
        reject('Error loading Google Maps API');
      };

      document.head.appendChild(script);
    });
  }

  private waitForGoogleMapsReady(resolve: () => void, reject: (reason?: any) => void): void {
    const timeoutMs = 10000;
    const intervalMs = 100;
    const startedAt = Date.now();

    const timer = window.setInterval(() => {
      if (this.isGoogleMapsReady()) {
        window.clearInterval(timer);
        this.apiLoaded = true;
        resolve();
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        reject('Timeout waiting for Google Maps API readiness');
      }
    }, intervalMs);
  }

  private isGoogleMapsReady(): boolean {
    return Boolean(
      typeof google !== 'undefined' &&
      google?.maps?.Geocoder &&
      google?.maps?.places?.Autocomplete
    );
  }

  private getGoogleApiKeyTelemetry(): { googleApiKeyLast6: string } {
    const key = String(this.activeApiKey || this.apiKey || '').trim();
    return {
      googleApiKeyLast6: key ? key.slice(-6) : 'empty',
    };
  }

  getPlacePredictions(input: string): Observable<any[]> {
    return new Observable(observer => {
      if (!this.autocompleteService) {
        observer.error('Servicio de autocompletado no inicializado');
        return;
      }

      this.autocompleteService.getPlacePredictions(
        {
          input: input,
          componentRestrictions: { country: 'us' },
          types: ['address']
        },
        (predictions: any[], status: string) => {
          this.ngZone.run(() => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              observer.next(predictions);
            } else {
              observer.next([]);
            }
          });
        }
      );
    });
  }

  getPlaceDetails(placeId: string): Observable<any> {
    return new Observable(observer => {
      this.placesService.getDetails(
        {
          placeId: placeId,
          fields: ['formatted_address', 'geometry', 'photos', 'name']
        },
        (place: any, status: string) => {
          this.ngZone.run(() => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              const result = {
                address: place.formatted_address,
                location: place.geometry.location,
                photoUrl: place.photos?.[0]?.getUrl({ maxWidth: 800 }) || null,
                name: place.name
              };
              this.placePhotoSubject.next(result.photoUrl);
              observer.next(result);
            } else {
              observer.error(`Error al obtener detalles: ${status}`);
            }
          });
        }
      );
    });
  }

  getPlacePhoto(): Observable<string | null> {
    return this.placePhotoSubject.asObservable();
  }

  initAutocomplete(inputElement: HTMLInputElement, onPlaceChanged?: (place: google.maps.places.PlaceResult) => void): google.maps.places.Autocomplete | null {
    if (!this.isGoogleMapsReady()) {
      return null;
    }

    this.autocompleteInput = inputElement;
    const autocomplete = new google.maps.places.Autocomplete(
      inputElement,
      {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['formatted_address', 'geometry', 'address_components']
      }
    );

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        
        if (!this.isValidUSAddress(place)) {
          inputElement.value = '';
          if (onPlaceChanged) {
            onPlaceChanged({} as google.maps.places.PlaceResult);
          }
          return;
        }
        
        if (place.photos?.[0]) {
          this.placePhotoSubject.next(place.photos[0].getUrl({ maxWidth: 800 }));
        }
        
        if (onPlaceChanged) {
          onPlaceChanged(place);
        }
      });
    });
    
    return autocomplete;
  }

  _initAutocomplete(inputElement: HTMLInputElement, onPlaceChanged?: (place: google.maps.places.PlaceResult) => void): google.maps.places.Autocomplete {
    
    
    this.autocompleteInput = inputElement;
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      types: ['geocode'],
    });

    // Set the placeholder (optional, can also be done via attribute)
    placeAutocomplete.placeholder = 'Search for a place...';

    // Append the element to your container
    inputElement.appendChild(placeAutocomplete);

    // Add an event listener for when a place is selected
    placeAutocomplete.addEventListener('gmp-placeselect', async (event: any) => {
      const placePrediction = event.placePrediction; // event object structure depends on the API version
      const place = placePrediction.toPlace();

      // Fetch additional fields as needed
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "addressComponents"],
      });

      // Use the place details (e.g., log to console or populate a form)
      console.log("Selected Place:", place.displayName, place.formattedAddress);
    });;
    
    return placeAutocomplete;
  }


  //#region Places New

  
  // Returns true when the Places New programmatic API (AutocompleteSuggestion) is available.
  private isPlacesNewReady(): boolean {
    return Boolean(
      typeof google !== 'undefined' &&
      (google as any)?.maps?.places?.AutocompleteSuggestion
    );
  }

  /**
   * Places New flow using AutocompleteSuggestion (programmatic API).
   * The original <input> is kept fully intact — same DOM, same CSS, same layout.
   * A lightweight custom dropdown <ul> is rendered below the input for suggestions.
   *
   * Falls back to legacy initAutocomplete() automatically when the API is unavailable.
   *
   * @param inputElement   Original <input> — untouched visually.
   * @param onPlaceSelected  Callback with a legacy-shaped PlaceResult.
   * @param debugLog       Optional logger, active only with developer=true.
   * @returns A cleanup() function that removes all listeners and the dropdown.
   */
  initAutocompletePlacesNew(inputElement: HTMLInputElement, onPlaceSelected: (place: google.maps.places.PlaceResult) => void, 
    debugLog?: (msg: string, ...args: any[]) => void
  ): (() => void) | null {

    // Fallback: API not available → delegate to legacy
    if (!this.isPlacesNewReady()) {
      debugLog?.('[MAPS-DEBUG] AutocompleteSuggestion not available – falling back to legacy initAutocomplete');
      return this.initAutocomplete(inputElement, onPlaceSelected) as any;
    }

    debugLog?.('[MAPS-DEBUG] Places New flow initialised (AutocompleteSuggestion)');

    // ── Dropdown element ────────────────────────────────────────────────────
    const dropdown = document.createElement('ul');
    dropdown.setAttribute('data-gmp-suggestions', 'true');
    dropdown.style.cssText = [
      'position:absolute',
      'z-index:10000',
      'background:#fff',
      'border:1px solid #ccc',
      'border-radius:4px',
      'box-shadow:0 2px 8px rgba(0,0,0,.2)',
      'list-style:none',
      'margin:0',
      'padding:4px 0',
      'max-height:260px',
      'overflow-y:auto',
      'display:none',
      'box-sizing:border-box',
    ].join(';');
    document.body.appendChild(dropdown);

    const positionDropdown = () => {
      const rect = inputElement.getBoundingClientRect();
      dropdown.style.top    = `${rect.bottom + window.scrollY}px`;
      dropdown.style.left   = `${rect.left   + window.scrollX}px`;
      dropdown.style.width  = `${rect.width}px`;
    };

    const hideDropdown = () => { dropdown.style.display = 'none'; };
    const showDropdown = () => { positionDropdown(); dropdown.style.display = 'block'; };

    // ── Debounce helper ─────────────────────────────────────────────────────
    let debounceTimer: any = null;
    const DEBOUNCE_MS = 300;

    // ── Select a suggestion ─────────────────────────────────────────────────
    const selectSuggestion = async (suggestion: any) => {
      hideDropdown();
      const inputText: string = suggestion.placePrediction?.text?.text ?? '';
      inputElement.value = inputText;
      debugLog?.('[MAPS-DEBUG] suggestion selected – fetching fields for:', inputText);

      try {
        const place = suggestion.placePrediction.toPlace();
        await place.fetchFields({
          fields: ['formattedAddress', 'location', 'addressComponents'],
        });

        const legacyShape = this.mapPlaceNewToLegacyShape(place);
        debugLog?.('[MAPS-DEBUG] mapped legacy shape:', legacyShape);

        if (!this.isValidUSAddress(legacyShape)) {
          debugLog?.('[MAPS-DEBUG] non-US address – clearing input');
          inputElement.value = '';
          this.ngZone.run(() => onPlaceSelected({} as google.maps.places.PlaceResult));
          return;
        }

        this.ngZone.run(() => onPlaceSelected(legacyShape));
      } catch (err) {
        debugLog?.('[MAPS-DEBUG] fetchFields error:', err);
        this.ngZone.run(() => onPlaceSelected({} as google.maps.places.PlaceResult));
      }
    };

    // ── Render suggestions list ─────────────────────────────────────────────
    const renderSuggestions = (suggestions: any[]) => {
      dropdown.innerHTML = '';
      if (!suggestions.length) { hideDropdown(); return; }

      suggestions.forEach(suggestion => {
        const text: string = suggestion.placePrediction?.text?.text ?? '';
        const li = document.createElement('li');
        li.textContent = text;
        li.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:14px;color:#333;';
        li.addEventListener('mouseenter', () => { li.style.background = '#f0f0f0'; });
        li.addEventListener('mouseleave', () => { li.style.background = ''; });
        // mousedown fires before blur so the dropdown stays open long enough to register click
        li.addEventListener('mousedown', (e) => {
          e.preventDefault(); // prevent input blur from hiding dropdown first
          selectSuggestion(suggestion);
        });
        dropdown.appendChild(li);
      });

      showDropdown();
    };

    // ── Input event handler ─────────────────────────────────────────────────
    const onInput = () => {
      clearTimeout(debounceTimer);
      const value = inputElement.value.trim();
      if (value.length < 3) { hideDropdown(); return; }

      debounceTimer = setTimeout(async () => {
        debugLog?.('[MAPS-DEBUG] fetchAutocompleteSuggestions for:', value);
        try {
          const { suggestions } = await (google as any).maps.places.AutocompleteSuggestion
            .fetchAutocompleteSuggestions({
              input: value,
              includedRegionCodes: ['us'],
            });
          debugLog?.('[MAPS-DEBUG] suggestions count:', suggestions?.length ?? 0);
          renderSuggestions(suggestions ?? []);
        } catch (err) {
          debugLog?.('[MAPS-DEBUG] fetchAutocompleteSuggestions error:', err);
          hideDropdown();
        }
      }, DEBOUNCE_MS);
    };

    // ── Close dropdown on outside click / blur ──────────────────────────────
    const onBlur = () => { setTimeout(hideDropdown, 150); };
    const onScroll = () => { if (dropdown.style.display !== 'none') positionDropdown(); };
    const onResize = () => { if (dropdown.style.display !== 'none') positionDropdown(); };

    inputElement.addEventListener('input', onInput);
    inputElement.addEventListener('blur', onBlur);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // ── Cleanup function returned to the caller ─────────────────────────────
    const cleanup = () => {
      clearTimeout(debounceTimer);
      inputElement.removeEventListener('input', onInput);
      inputElement.removeEventListener('blur', onBlur);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      dropdown.remove();
      debugLog?.('[MAPS-DEBUG] Places New flow cleaned up');
    };

    return cleanup;
  }

  /**
   * Converts a Places New `Place` object (after fetchFields) to the shape
   * expected by existing setFullAddress / setHouseImage logic.
   */
  mapPlaceNewToLegacyShape(place: any): google.maps.places.PlaceResult {
    const address_components: google.maps.GeocoderAddressComponent[] = (
      place.addressComponents ?? []
    ).map((c: any) => ({
      long_name: c.longText ?? '',
      short_name: c.shortText ?? '',
      types: c.types ?? [],
    }));

    return {
      formatted_address: place.formattedAddress ?? '',
      address_components,
      geometry: {
        location: place.location as unknown as google.maps.LatLng,
        viewport: undefined as unknown as google.maps.LatLngBounds,
      },
    } as google.maps.places.PlaceResult;
  }

  //#endregion Places New

  private isValidUSAddress(place: google.maps.places.PlaceResult): boolean {
    if (!place.address_components) return false;
    
    // Verificar que el país sea 'US'
    const countryComponent = place.address_components.find(
      component => component.types.includes('country')
    );
    
    return countryComponent?.short_name === 'US';
  }

  geocodeAddress(
    address: string,
    zip: string = "",
    debugLog?: (msg: string, ...args: any[]) => void,
    source: string = 'unknown'
  ): Promise<google.maps.GeocoderResult | undefined> {
    const timer = this.telemetryService.startTimer();
    const keyTelemetry = this.getGoogleApiKeyTelemetry();
    if (!this.isGoogleMapsReady()) {
      debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_MAPS_NOT_READY', { source, address, zip });
      this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), false, undefined, { source, resultType: 'maps_not_ready', ...keyTelemetry });
      this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'maps_not_ready', true, { source, ...keyTelemetry });
      return Promise.resolve(undefined);
    }

    if(address && address.trim() !== '') {
      const geocoder = new google.maps.Geocoder();

      var _componentRestrictions: any = { country: 'us' };

      if(zip && zip.trim() !== '') {
        _componentRestrictions = { ..._componentRestrictions, postalCode: zip };
      }

      this.geocodeRequestCount += 1;
      this.geocodeRequestBySource[source] = (this.geocodeRequestBySource[source] || 0) + 1;

      debugLog?.(`🌐 [ADDR] API_CALL #${this.geocodeRequestCount}`, {
        source,
        serviceTotalRequests: this.geocodeRequestCount,
        serviceRequestsBySource: { ...this.geocodeRequestBySource },
        address,
        zip,
        componentRestrictions: _componentRestrictions,
      });


      return new Promise((resolve) => {
        geocoder.geocode({ address, language: 'en', componentRestrictions: _componentRestrictions }, (results: any[], status: any) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {          
            if (this.isValidUSAddress(results[0])) {
              debugLog?.('[MAPS-DEBUG] [ADDR] API_OK', {
                source,
                status,
                formattedAddress: results?.[0]?.formatted_address,
              });
              this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), true, 200, { source, resultType: 'ok', ...keyTelemetry });
              this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'ok', true, { source, ...keyTelemetry });
              resolve(results[0]);
            } else {
              console.log('Address is not in the USA');
              debugLog?.('[MAPS-DEBUG] [ADDR] API_REJECTED_NON_US', { source, status });
              this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), false, undefined, { source, resultType: 'non_us', ...keyTelemetry });
              this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'non_us', true, { source, ...keyTelemetry });
              resolve(undefined);
            }
          } else if (status === 'ZERO_RESULTS') {
            console.log('No results found for address in USA');
            debugLog?.('[MAPS-DEBUG] [ADDR] API_ZERO_RESULTS', { source, status, address, zip });
            this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), false, 404, { source, resultType: 'zero_results', ...keyTelemetry });
            this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'zero_results', true, { source, ...keyTelemetry });
            resolve(undefined);
          } else {
            console.error('Geocoding error:', status);
            debugLog?.('[MAPS-DEBUG] [ADDR] API_ERROR', { source, status, address, zip });
            this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), false, undefined, { source, resultType: 'error', ...keyTelemetry });
            this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'error', true, { source, ...keyTelemetry });
            resolve(undefined);
          }
        });
      });
    }
    debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_EMPTY_ADDRESS', { source, address, zip });
    this.telemetryService.trackApiCallResult('GoogleGeocode', 'https://maps.googleapis.com/maps/api/geocode', this.telemetryService.stopTimer(timer), false, undefined, { source, resultType: 'empty_address', ...keyTelemetry });
    this.telemetryService.trackAddressResolutionResult('google_geocode_api', this.telemetryService.stopTimer(timer), 'empty_address', false, { source, ...keyTelemetry });
    return Promise.resolve(undefined);
  }
}


